import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { LeafletACMap } from "../../../../components";
import { safe_get } from "../../../../report-visuals";
import { useStore } from "../../../../helpers";
import type { SourceFile } from "../sidebar/sources-types";
import { copyTableAsPng } from "../../../survey-management/copy-table-image";
import TARSChartCopyImageToolbar from "../../tars-charts/TARSChartCopyImageToolbar";
import DataBankEmptyState from "./DataBankEmptyState";
import { isValidGeoJsonMap } from "./dataBankDataGuards";

type Props = { file: SourceFile };

const MAP_CONFIG = { height: 650, show_booth_numbers: true };

export default function DataBankConstituencyMapPanel({ file }: Props) {
    const { flexiStore } = useStore();
    const fsdId = file.data_bank_fsd_id ?? 0;
    const mapKey = file.data_bank_map_key ?? "";
    const snapshotRef = useRef<HTMLDivElement>(null);
    const copyImageRef = useRef<(() => Promise<void>) | null>(null);

    const { data: geojson, isLoading, isError } = useQuery({
        queryKey: ["tars-data-bank-map", fsdId, mapKey],
        retry: false,
        queryFn: async () => {
            try {
                if (fsdId <= 0 || !mapKey) return null;
                await flexiStore.get_fsdata(fsdId);
                const maps = safe_get(flexiStore.current_fsd?.r1, "maps", null);
                if (!maps || typeof maps !== "object" || Array.isArray(maps)) return null;
                const path = (maps as Record<string, unknown>)[mapKey];
                if (!path || typeof path !== "string") return null;
                await flexiStore.load_s3_json_resource("urn-fsd", path, "tars_databank_map_view");
                const resource = flexiStore.external_resource;
                return isValidGeoJsonMap(resource) ? resource : null;
            } catch {
                return null;
            }
        },
    });

    useEffect(() => {
        if (!geojson) {
            copyImageRef.current = null;
            return;
        }
        copyImageRef.current = async () => {
            const el = snapshotRef.current;
            if (!el) {
                toast.error("Nothing to copy yet.");
                return;
            }
            if (!navigator.clipboard?.write) {
                toast.error("Clipboard is not available (use HTTPS or localhost).");
                return;
            }
            try {
                const dpr = typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1;
                const pixelRatio = Math.min(5, Math.max(4, dpr * 2));
                await copyTableAsPng(el, { pixelRatio, skipAutoScale: true });
                toast.success("Constituency map copied as image — paste into documents or chat");
            } catch {
                toast.error("Could not copy image to clipboard");
            }
        };
    }, [geojson]);

    if (isLoading) {
        return (
            <div className="flex min-h-[200px] flex-1 items-center justify-center gap-2 text-sm text-gray-500">
                <Oval height={32} width={32} color="#64748b" secondaryColor="#e2e8f0" visible />
                Loading map…
            </div>
        );
    }
    if (isError) {
        return (
            <DataBankEmptyState
                variant="error"
                message="Something went wrong while loading the map. Try again later."
            />
        );
    }
    if (!geojson) {
        return (
            <DataBankEmptyState
                message={
                    mapKey
                        ? `No map data is available for "${mapKey}" in this assembly constituency.`
                        : "No constituency map is configured for this source."
                }
            />
        );
    }

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 bg-white/90 px-4 py-3">
                <p className="text-[12px] font-medium text-slate-500">
                    Constituency map · <span className="text-slate-700">{mapKey}</span>
                </p>
                <TARSChartCopyImageToolbar
                    copyRef={copyImageRef}
                    buttonLabel="Copy as image"
                    ariaLabel="Copy constituency map as image"
                />
            </div>
            <div className="p-4">
                <div
                    ref={snapshotRef}
                    className="rounded-lg border border-gray-100 bg-white p-4"
                    style={{ overflow: "visible" }}
                >
                    <div className="mb-3 border-b border-gray-100 pb-2 text-lg font-bold uppercase text-gray-900">
                        {mapKey}
                    </div>
                    <LeafletACMap geojson={geojson} config={MAP_CONFIG} />
                </div>
            </div>
        </div>
    );
}
